/**
 * Orders Page
 * Modern, professional design with improved UX
 */

import { useEffect, useRef, useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  useOrders,
  ORDER_STATUSES,
  getStatusColor,
  getPriorityColor,
  formatOrderAmount,
  formatOrderDate,
  type Order,
  type OrderStatus,
  type OrderPriority,
} from '@/hooks/useOrders'
import { usePrefetchOrderDetails } from '@/hooks/useOrderDetails'
import { decodeOrderId } from '@/lib/utils'
import {
  Plus,
  Search,
  Package,
  Filter,
  ChevronRight,
  Calendar,
  User,
  AlertCircle,
  RefreshCw,
} from 'lucide-react'
import { useLocalization } from '@/contexts/LocalizationContext'
import { useProfileSwitch } from '@/contexts/ProfileSwitchContext'

/**
 * Orders Page Component
 */
export function OrdersPage() {
  const { t } = useLocalization()
  const navigate = useNavigate()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const { canAccessBusinessClientOrderCreation, isBusinessClientMode } = useProfileSwitch()

  // Local filter state
  const [statusFilter, setStatusFilter] = useState<OrderStatus | null>(null)
  const [searchQuery, setSearchQuery] = useState<string>('')

  // Fetch orders with filters
  const {
    orders,
    isLoading,
    isFetchingNextPage,
    isError,
    error,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useOrders({
    status: statusFilter || undefined,
    search: searchQuery || undefined,
  })

  // Handle load more click
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement

      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        if (e.key === 'Escape') {
          ;(target as HTMLInputElement).blur()
        }
        return
      }

      if (
        (e.key === 'n' || e.key === 'N') &&
        !e.metaKey &&
        !e.ctrlKey &&
        canAccessBusinessClientOrderCreation
      ) {
        e.preventDefault()
        navigate('/create-order')
        return
      }

      if (e.key === '/') {
        e.preventDefault()
        searchInputRef.current?.focus()
        return
      }

      if ((e.key === 'r' || e.key === 'R') && !e.metaKey && !e.ctrlKey) {
        e.preventDefault()
        refetch()
        return
      }

      const key = parseInt(e.key, 10)
      if (key >= 1 && key <= 9 && orders.length >= key) {
        e.preventDefault()
        const order = orders[key - 1]
        const decodedId = decodeOrderId(order.id)
        navigate(`/orders/${decodedId}`)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate, orders, refetch, canAccessBusinessClientOrderCreation])

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('orders.title')}</h1>
              <p className="mt-1 text-sm text-gray-500 hidden sm:block">
                {t('orders.subtitle')}
              </p>
            </div>
            {canAccessBusinessClientOrderCreation && (
              <Button
                onClick={() => navigate('/create-order')}
                className="gap-2 bg-brand-primary hover:opacity-90 flex-shrink-0"
                size="sm"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">{t('orders.newOrder')}</span>
                <span className="sm:hidden">{t('orders.newOrderShort')}</span>
              </Button>
            )}
          </div>

        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        {/* Filters Bar */}
        <div className="mb-4 sm:mb-6 flex flex-col gap-3 sm:gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col sm:flex-row flex-1 gap-3 sm:items-center">
            {/* Search */}
            <div className="relative flex-1 sm:max-w-md">
              <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('orders.searchPlaceholder')}
                className="ps-10 pe-12 h-10 bg-white"
              />
              <kbd className="absolute end-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex h-5 items-center gap-1 rounded border bg-gray-100 px-1.5 text-[10px] font-medium text-gray-500">
                /
              </kbd>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400 flex-shrink-0" />
              <select
                value={statusFilter ?? ''}
                onChange={(e) => setStatusFilter((e.target.value || null) as OrderStatus | null)}
                className="h-10 w-full sm:w-auto rounded-lg border border-gray-200 bg-white px-3 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
              >
                <option value="">{t('orders.allStatuses')}</option>
                {ORDER_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`status.${status}`)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Results count */}
          {!isLoading && (
            <p className="text-sm text-gray-500">
              {orders.length === 1
                ? t('orders.ordersFound', { count: 1 })
                : t('orders.ordersFoundPlural', { count: orders.length })}
            </p>
          )}
        </div>

        {/* Error State */}
        {isError && (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <div>
                <p className="font-medium text-red-800">{t('orders.errorLoading')}</p>
                <p className="text-sm text-red-600">{error?.message || t('common.somethingWentWrong')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-lg" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                  <Skeleton className="h-6 w-20 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && orders.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 bg-white py-16">
            <div className="rounded-full bg-gray-100 p-4">
              <Package className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900">{t('orders.noOrdersFound')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchQuery || statusFilter
                ? t('orders.adjustFilters')
                : t('orders.getStarted')}
            </p>
            {!searchQuery && !statusFilter && canAccessBusinessClientOrderCreation && (
              <Button
                onClick={() => navigate('/create-order')}
                className="mt-4 gap-2 bg-brand-primary hover:opacity-90"
              >
                <Plus className="h-4 w-4" />
                {t('orders.createOrder')}
              </Button>
            )}
          </div>
        )}

        {/* Orders List */}
        {!isLoading && orders.length > 0 && (
          <div className="space-y-3">
            {orders.map((order, index) => {
              const decodedId = decodeOrderId(order.id)
              return (
                <OrderCard
                  key={order.id}
                  order={order}
                  index={index}
                  decodedId={decodedId}
                  isBusinessClientMode={isBusinessClientMode}
                  onClick={() => navigate(`/orders/${decodedId}`)}
                />
              )
            })}

            {/* Load More */}
            {hasNextPage && (
              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={isFetchingNextPage}
                  className="min-w-[200px] gap-2"
                >
                  {isFetchingNextPage ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    t('orders.loadMoreOrders')
                  )}
                </Button>
              </div>
            )}

            {!hasNextPage && orders.length > 5 && (
              <p className="text-center text-sm text-gray-400 pt-4">
                {t('orders.reachedEnd')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Order Card Component
 */
function OrderCard({
  order,
  index,
  decodedId,
  isBusinessClientMode,
  onClick,
}: {
  order: Order
  index: number
  decodedId: string
  isBusinessClientMode: boolean
  onClick: () => void
}) {
  const { t } = useLocalization()
  const prefetch = usePrefetchOrderDetails()
  const normalizedStatus = (order.status?.toLowerCase() || 'draft') as OrderStatus
  const normalizedPriority = (order.priority?.toLowerCase() || 'normal') as OrderPriority
  const shortcutKey = index < 9 ? index + 1 : undefined
  const amountLabel = isBusinessClientMode
    ? t('orderDetails.totalAmount')
    : t('orderDetails.providerEarningsTotal')
  const amountValue = isBusinessClientMode
    ? formatOrderAmount(order.totalAmount, order.currency || undefined)
    : order.providerEarningsTotal !== null
      ? formatOrderAmount(
          order.providerEarningsTotal,
          order.providerEarningsCurrency || order.currency || undefined
        )
      : t('common.noData')

  return (
    <div
      className="group relative rounded-xl border border-gray-200 bg-white p-4 transition-all hover:border-brand-primary/30 hover:shadow-md cursor-pointer"
      onClick={onClick}
      onMouseEnter={() => prefetch(decodedId)}
    >
      <div className="flex items-center gap-4">
        {/* Order Icon / Number Badge */}
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-primary/10 text-brand-primary font-bold text-sm">
          {shortcutKey ? (
            <span className="flex flex-col items-center">
              <span className="text-xs text-brand-secondary">#{shortcutKey}</span>
            </span>
          ) : (
            <Package className="h-5 w-5" />
          )}
        </div>

        {/* Order Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 truncate">
              {order.orderNumber}
            </h3>
            <Badge className={getStatusColor(normalizedStatus)}>
              {t(`status.${normalizedStatus}`) || order.status}
            </Badge>
            {order.isOverdue && (
              <Badge variant="destructive" className="text-[10px]">
                {t('common.overdue')}
              </Badge>
            )}
            {normalizedPriority !== 'normal' && (
              <Badge className={getPriorityColor(normalizedPriority)}>
                {t(`status.${normalizedPriority}`)}
              </Badge>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <User className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate max-w-[120px] sm:max-w-none">{order.clientName || t('orders.noClient')}</span>
            </span>
            {order.beneficiaryName && (
              <span className="hidden sm:flex items-center gap-1">
                <User className="h-3.5 w-3.5 flex-shrink-0" />
                {order.beneficiaryName}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
              {formatOrderDate(order.submittedAt || order.createdAt)}
            </span>
          </div>
        </div>

        {/* Right Side Info */}
        <div className="hidden sm:flex items-center gap-6">
          <div className="text-center">
            <p className="text-xs text-gray-400">{t('orders.services')}</p>
            <p className="font-semibold text-gray-900">{order.totalServices}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-400">{t('orders.appointments')}</p>
            <p className="font-semibold text-gray-900">{order.totalAppointments}</p>
          </div>
          <div className="text-end min-w-[100px]">
            <p className="text-xs text-gray-400">{amountLabel}</p>
            <p className="font-bold text-gray-900">{amountValue}</p>
          </div>
        </div>

        {/* Arrow */}
        <ChevronRight className="h-5 w-5 text-gray-300 group-hover:text-brand-primary transition-colors" />
      </div>

      {/* Mobile: Additional Info */}
      <div className="mt-3 flex items-center justify-between sm:hidden border-t border-gray-100 pt-3">
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-500">
            {t('orders.servicesAppointments', { services: order.totalServices, appointments: order.totalAppointments })}
          </span>
        </div>
        <p className="font-bold text-gray-900">{amountValue}</p>
      </div>
    </div>
  )
}

export default OrdersPage
